import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { Input } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import RadioGroup from '@material-ui/core/RadioGroup';

import { loadBasePrices } from './actions/load-base-prices';
import { stripeBasePricesSelectorV3 } from '../../account-selectors';

import { getCalculatedItemMonthlyCost } from './subscription-helpers';

import { isValueANumber } from '../../../util/number-utils';
import { isSubscriptionCadence, SubscriptionCadence } from '../../../util/subscription-cadence';
import { LabeledRadioButton } from '../../../widgets/labeled-radio-button';

type InputProps = {
  subscriptionCadence: SubscriptionCadence;
  setSubscriptionCadence: (subscriptionCadence: SubscriptionCadence) => void;
  activeJobCount: number | null;
  changeJobCount: (employeeCount: string | null) => void;
  activeEmployeeCount: number | null;
  changeEmployeeCount: (employeeCount: string | null) => void;
  additionalOrgCount: number | null;
  changeAdditionalOrgCount: (employeeCount: string | null) => void;
  accountMissingPaymentInfo?: boolean;
};

const mapStateToProps = (state: any) => ({
  basePrices: stripeBasePricesSelectorV3(state),
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    getStripeBasePrices: () => dispatch(loadBasePrices()),
  },
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

export const ItemizedPlanSelectorBody = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const {
    subscriptionCadence,
    setSubscriptionCadence,
    activeJobCount,
    activeEmployeeCount,
    additionalOrgCount,
    basePrices,
    changeJobCount,
    changeEmployeeCount,
    changeAdditionalOrgCount,
    accountMissingPaymentInfo,
    actions,
  } = props;

  useEffect(() => {
    actions.getStripeBasePrices();
  }, [actions]);

  const activeBasePrices = basePrices || [];

  const handleChangeCadence = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSubscriptionCadence = event.target.value;
    if (newSubscriptionCadence && isSubscriptionCadence(newSubscriptionCadence)) {
      setSubscriptionCadence(newSubscriptionCadence);
    }
  };

  const handleChangeEmployeeCount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEmployeeCount = isValueANumber(event.target.value) ? event.target.value : null;
    changeEmployeeCount(newEmployeeCount);
  };

  const handleChangeJobCount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newJobCount = isValueANumber(event.target.value) ? event.target.value : null;
    changeJobCount(newJobCount);
  };

  const handleChangeAdditionalOrgCount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newOrgCount = isValueANumber(event.target.value) ? event.target.value : null;
    changeAdditionalOrgCount(newOrgCount);
  };

  const baseSubscriptionCost = getCalculatedItemMonthlyCost(
    activeBasePrices,
    1,
    'baseSubscription',
  );
  const employeeCost = getCalculatedItemMonthlyCost(
    activeBasePrices,
    activeEmployeeCount,
    'activeEmployees',
  );
  const jobCost = getCalculatedItemMonthlyCost(activeBasePrices, activeJobCount, 'activeJobs');
  const additionalOrgCost = getCalculatedItemMonthlyCost(
    activeBasePrices,
    additionalOrgCount,
    'additionalOrgs',
  );

  const totalCost =
    (baseSubscriptionCost || 0) + (employeeCost || 0) + (jobCost || 0) + (additionalOrgCost || 0);

  return (
    <>
      <FormControl margin="dense" className="u-width-100">
        <FormHelperText className="u-margin-top-xs">Cadence</FormHelperText>
        <RadioGroup
          value={subscriptionCadence}
          onChange={handleChangeCadence}
          className="u-margin-y-md"
          name="base-cadence"
        >
          <LabeledRadioButton
            value={SubscriptionCadence.MONTHLY}
            label="Billed Monthly"
            subtext={`(${
              accountMissingPaymentInfo ? 'Credit card information will be collected; ' : ''
            }charged automatically)`}
          />
        </RadioGroup>
      </FormControl>
      <div className="u-width-250px">Monthly Rate (Ignoring discounts)</div>
      <div className="u-margin-left-lg typo-subtitle">
        <div>Base Subscription: ${baseSubscriptionCost === null ? '--' : baseSubscriptionCost}</div>
        <div className="u-display-flex u-justifyContent-spaceBetween u-alignItems-flexEnd">
          <div>Active Employees: ${employeeCost === null ? '--' : employeeCost}</div>
          <div>
            <Input
              value={activeEmployeeCount || ''}
              onChange={handleChangeEmployeeCount}
              type="number"
            />
          </div>
        </div>
        <div className="u-display-flex u-justifyContent-spaceBetween u-alignItems-flexEnd">
          <div>Active Jobs: ${jobCost === null ? '--' : jobCost}</div>
          <Input value={activeJobCount || ''} onChange={handleChangeJobCount} type="number" />
        </div>
        <div className="u-display-flex u-justifyContent-spaceBetween u-alignItems-flexEnd">
          <div>
            Additional Organizations: ${additionalOrgCost === null ? '--' : additionalOrgCost}
          </div>
          <Input
            value={additionalOrgCount || ''}
            onChange={handleChangeAdditionalOrgCount}
            type="number"
          />
        </div>
      </div>
      <div className="u-margin-y-lg typo-bold">Total: ${totalCost}</div>
    </>
  );
});
